'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { type AppRole, ROLE_META } from '@/lib/permissions';

const ALL_ROLES: AppRole[] = [
  'Procurement Officer',
  'IT Security',
  'Privacy Officer',
  'City Attorney',
  'Finance Director',
  'CISO',
  'Admin',
];

export const ROLE_NAMES: Record<AppRole, string> = {
  'Procurement Officer': 'Marcus Webb',
  'IT Security':         'Jordan Kim',
  'Privacy Officer':     'Priya Nair',
  'City Attorney':       'Robert Chen',
  'Finance Director':    'Sandra Liu',
  CISO:                  'Alex Torres',
  Admin:                 'System Admin',
};

export default function RoleSwitcher() {
  const { role, setRole, roleMeta } = useRole();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))] bg-[hsl(var(--background))] text-[hsl(var(--text-secondary))] transition-all duration-150 text-xs"
        title="Switch active role"
      >
        <ShieldCheck size={12} className="text-[hsl(var(--accent))]" />
        <span className="font-medium text-[hsl(var(--text-primary))]">{role}</span>
        <ChevronDown size={11} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 card shadow-md z-50 animate-slide-up py-1">
          <p className="px-3 py-1.5 text-2xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide border-b border-[hsl(var(--border))] mb-1">
            Active Role
          </p>
          {ALL_ROLES.map((r) => {
            const meta = ROLE_META[r];
            const active = r === role;
            return (
              <button
                key={r}
                onClick={() => { setRole(r); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[hsl(var(--background))] transition-colors ${active ? 'bg-[hsl(var(--accent-light))]' : ''}`}
              >
                <div className={`w-6 h-6 rounded-full ${meta.color} flex items-center justify-center text-white text-2xs font-semibold flex-shrink-0`}>
                  {meta.initials}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-medium truncate ${active ? 'text-[hsl(var(--accent))]' : 'text-[hsl(var(--text-primary))]'}`}>{r}</p>
                  <p className="text-2xs text-[hsl(var(--text-muted))] truncate">{ROLE_NAMES[r]}</p>
                </div>
                {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
